import clsx from "clsx";
import React from "react";
import CategoryFilterItem from "web/react/components/category-filter-item/category-filter-item";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import { FeedFilterColors } from "web/react/components/feeds-filters/feed-filter-colors/feed-filter-colors";
import { FeedFilterSizes } from "web/react/components/feeds-filters/feed-filter-sizes/feed-filter-sizes";
import { FeedsFiltersPairOfSelects } from "web/react/components/feeds-filters/feeds-filters-pair-of-selects/feeds-filters-pair-of-selects";
import FilterOptionGroup from "web/react/components/filter-option-group/filter-option-group";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import {
    AllSelectedFilterInfo,
    SelectedFilterInfo,
    StandardFilterProps,
} from "web/types/feed-filters";
import {
    CategoryFilterOption,
    InputFilterOption,
    isCategorySection,
    isFilterSection,
    UniversalFilterSection,
} from "web/types/universal-filters";
import ChevronFilterItem from "./chevron-filter-item";
import styles from "./filter-pagination.module.css";
import { PaginatedItem, PaginationExpansionState, PaginationProps } from "./pagination-state";

/**
 * Category should be its own facet component it gets too confusing when working
 * with facet groups and categories in the same component
 */
interface FeedsFiltersCategoryProps extends StandardFilterProps, PaginationProps {
    item: PaginatedItem;
    selectedFilterInfo: SelectedFilterInfo;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    categorySectionOpen: boolean;
}

function FeedsFiltersCategory({
    item,
    onPageChange,
    paginationState,
    categorySectionOpen,
    ...props
}: FeedsFiltersCategoryProps): JSX.Element {
    let expanded = paginationState.getExpansionState(item);
    let isSection = isFilterSection(item);
    let expandableId = "paginated_item_" + paginationState.getKey(item);
    const subText = getSubText(item, props.selectedFilterInfo, isSection);
    const breadcrumbs = props.selectedFilterInfo.breadcrumbs;

    const children = (
        <div
            className={clsx(styles.expandable, {
                [styles.expandedActive]: expanded == PaginationExpansionState.ExpandedActive,
                [styles.expandedInactive]: expanded == PaginationExpansionState.ExpandedInactive,
                [styles.collapsed]: expanded == PaginationExpansionState.Collapsed,
            })}
            id={expandableId}
            aria-hidden={expanded === PaginationExpansionState.Collapsed}
        >
            {breadcrumbs.length === 0 ? null : (
                <PaginationItemChildren
                    {...{
                        item,
                        onPageChange,
                        paginationState,
                        categorySectionOpen,
                        ...props,
                    }}
                />
            )}
        </div>
    );

    if (environment.getFeature("seo_enable_relevant_feed_filters") && item.checked) {
        return (
            <div
                className={clsx(styles.paginationItem, {
                    [styles.sectionItem]: isSection,
                })}
            >
                {children}
            </div>
        );
    }

    return (
        <li
            className={clsx(styles.paginationItem, {
                [styles.sectionItem]: isSection,
            })}
        >
            <ChevronFilterItem
                item={item}
                large={isSection}
                onClick={onPageChange}
                expanded={expanded != PaginationExpansionState.Collapsed}
                subText={subText}
                ariaControls={expandableId}
            />
            {children}
        </li>
    );
}

interface PaginationItemProps extends StandardFilterProps, PaginationProps {
    item: PaginatedItem;
    selectedFilterInfo: SelectedFilterInfo;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    categorySectionOpen: boolean;
}

function PaginationItemChildren({
    item,
    onPageChange,
    paginationState,
    categorySectionOpen,
    ...props
}: PaginationItemProps): JSX.Element {
    let children: (CategoryFilterOption | InputFilterOption)[];

    if (item.type == "category") {
        children = item.children;
    } else {
        if (item.type == "pair_of_selects") {
            return <FeedsFiltersPairOfSelects section={item} {...props} />;
        }
        if (item.type === "sizes") {
            return <FeedFilterSizes item={item} {...props} />;
        }
        if (item.type === "colors") {
            return <FeedFilterColors item={item} {...props} />;
        }
        let optionGroup = item.option_groups[0];
        if (optionGroup.type == "filtered_scroll_area") {
            return (
                <FilterOptionGroup
                    isVisible={
                        paginationState.getExpansionState(item) ==
                        PaginationExpansionState.ExpandedActive
                    }
                    {...{ optionGroup, ...props }}
                />
            );
        }
        children = optionGroup.options;
    }

    let hasChildren = children.some(
        (item) =>
            item.type == "category" &&
            item.children.length > 0 &&
            props.pendingFilterInfo.getChecked(item)
    );

    let goBackString: string;

    if ("id" in item && isCategorySection(item) && item.id!.startsWith("taxonomy-")) {
        // "More {product_category}"
        goBackString = gettext("related_links.designers_selling_this_product_category_title", {
            product_category: item.label,
        });
    }

    let reactChildren = children.map((item) => {
        let key = item.name + "=" + item.value.toString();
        if (item.type == "category" && item.name == "category") {
            return (
                <CategoryFilterItem
                    goBackString={goBackString}
                    siblingOpen={hasChildren}
                    {...{ item, key, ...props }}
                />
            );
        } else if (item.type == "category") {
            return (
                <FeedsFiltersCategory
                    {...{
                        item,
                        key,
                        onPageChange,
                        paginationState,
                        categorySectionOpen,
                        ...props,
                    }}
                />
            );
        } else {
            return (
                <li key={key}>
                    <CheckboxFilterItem {...{ item, ...props }} />
                </li>
            );
        }
    });

    if (
        environment.getFeature("seo_enable_relevant_feed_filters") &&
        hasChildren &&
        (item.type == "category" || item.id === "section-category")
    ) {
        return <>{reactChildren}</>;
    }

    return (
        <ol
            className={clsx(styles.children, {
                [styles.subChildOpen]: hasChildren,
            })}
        >
            {reactChildren}
        </ol>
    );
}

function getSubText(
    item: PaginatedItem,
    selectedFilterInfo: SelectedFilterInfo,
    isSection: boolean
): string | undefined {
    if (!selectedFilterInfo) {
        return;
    }
    if (item.type == "pair_of_selects") {
        let crumb = selectedFilterInfo.breadcrumbs[0];
        if (crumb) {
            return `- ${crumb.label}`;
        }
    } else if (isSection) {
        let breadcrumbs = selectedFilterInfo.breadcrumbs;

        if (breadcrumbs.length == 0) {
            return;
        }

        if (breadcrumbs[0].name != "product_type") {
            return breadcrumbs.map((item) => `- ${item.label}`).join(" ");
        }

        let lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
        if (lastBreadcrumb.type == "category" && lastBreadcrumb.children.length != 0) {
            // "All {category_name}"
            return `- ${gettext("feed.filters.all_categories_tooltip", {
                category_name: lastBreadcrumb.label,
            })}`;
        } else if (lastBreadcrumb.type == "category" && lastBreadcrumb.children.length == 0) {
            return `- ${lastBreadcrumb.label}`;
        }

        return breadcrumbs
            .map((item) => {
                if (item.type == "checkbox" || item.type == "radio") {
                    return `- ${item.label}`;
                }
            })
            .filter((item) => item)
            .join(" ");
    } else if (item.type == "category") {
        let count = selectedFilterInfo.counts[item.name + "=" + item.value];
        if (count) {
            return `- ${gettext("feed.filters.mobile_selected_count", {
                count: count,
            })}`;
        }
    }
}

function PaginationItem({
    item,
    onPageChange,
    paginationState,
    categorySectionOpen,
    ...props
}: PaginationItemProps): JSX.Element {
    let expanded = paginationState.getExpansionState(item);
    let isSection = isFilterSection(item);
    const shouldRender = "should_render" in item && item.should_render;

    let expandableId = "paginated_item_" + paginationState.getKey(item);
    const subText = getSubText(item, props.selectedFilterInfo, isSection);

    return (
        <div
            className={clsx(styles.paginationItem, {
                [styles.sectionItem]: isSection,
            })}
        >
            <ChevronFilterItem
                item={item}
                large={isSection}
                onClick={onPageChange}
                expanded={expanded != PaginationExpansionState.Collapsed}
                subText={subText}
                ariaControls={expandableId}
            />
            {shouldRender || expanded ? (
                <div
                    className={clsx(styles.expandable, {
                        [styles.expandedActive]:
                            expanded == PaginationExpansionState.ExpandedActive,
                        [styles.expandedInactive]:
                            expanded == PaginationExpansionState.ExpandedInactive,
                        [styles.collapsed]: expanded == PaginationExpansionState.Collapsed,
                        [styles.scrollable]: item.id === "section-sizes",
                    })}
                    id={expandableId}
                    role="group"
                    aria-labelledby={`${expandableId}-header`}
                    aria-hidden={expanded === PaginationExpansionState.Collapsed}
                >
                    <PaginationItemChildren
                        {...{ item, onPageChange, paginationState, categorySectionOpen, ...props }}
                    />
                </div>
            ) : null}
        </div>
    );
}

interface FilterPaginationProps extends StandardFilterProps, PaginationProps {
    allSelectedFilterInfo: AllSelectedFilterInfo;
    sections: UniversalFilterSection[];
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
}

const FilterPagination = React.forwardRef<HTMLDivElement, FilterPaginationProps>(
    function FilterPagination(
        { allSelectedFilterInfo, onPageChange, sections, ...props },
        ref
    ): JSX.Element {
        let hasExpandedChildren = props.paginationState.hasActivePage();
        let activePage = props.paginationState.getActivePage();
        return (
            <div
                className={clsx(styles.container, {
                    [styles.expandedActive]: !hasExpandedChildren,
                    [styles.expandedInactive]: hasExpandedChildren,
                })}
            >
                <div className={styles.children} ref={ref}>
                    {sections.map((section, index) => {
                        let categorySectionOpen = false;

                        if (
                            hasExpandedChildren &&
                            section.id == "section-category" &&
                            activePage &&
                            "id" in activePage &&
                            activePage.id != "section-category"
                        ) {
                            categorySectionOpen = true;
                        }

                        // ORG-2894, ORG-2940, ORG-2941
                        // Test: Show only relevant category feed filters
                        // We want to hide the whole section if there are no children of the currently selected taxonomy
                        if (
                            section.id === "section-category" &&
                            environment.getFeature("seo_enable_relevant_feed_filters")
                        ) {
                            const { options: productTypes } = section.option_groups[0];
                            const selectedProductType = (
                                productTypes as CategoryFilterOption[]
                            ).find((productType) => productType.checked);
                            const categories = selectedProductType?.children;

                            // The selected product type doesn't have categories
                            if (productTypes.length === 1 && categories && !categories.length) {
                                return null;
                            }

                            const selectedCategory = categories?.find(
                                (category) => category.checked
                            );
                            const subcategories = (selectedCategory as CategoryFilterOption)
                                ?.children;

                            // The selected product category doesn't have subcategories
                            if (
                                categories?.length === 1 &&
                                subcategories &&
                                !subcategories.length
                            ) {
                                return null;
                            }
                        }

                        return (
                            <PaginationItem
                                categorySectionOpen={categorySectionOpen}
                                key={section.id}
                                item={section}
                                onPageChange={(item): void => onPageChange(item, index)}
                                selectedFilterInfo={allSelectedFilterInfo[section.id]}
                                {...props}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }
);

export default FilterPagination;
